import React, { lazy, Suspense, useState, useEffect } from 'react';
import { formSchema, type FormData } from '../types/form';
import {
  formatPhoneNumber,
  validateEmail,
  validatePhone,
} from '../utils/validation';
import { FormController } from '../controllers/FormController';
import { storageController } from '../controllers/StorageController';
import {
  FormRouteManager,
  FormRouteEnum,
  type FormRoute,
  useFormRouting,
} from '../routes/FormRoutes';
import { marketingParams } from '../utils/marketingParams';
import StartForm from './forms/StartForm';
import LoanPurposeForm from './forms/LoanPurposeForm';
import LoanAmountForm from './forms/LoanAmountForm';
import PropertyStatusForm from './forms/PropertyStatusForm';
import EmploymentStatusForm from './forms/EmploymentStatusForm';
import EmploymentFrequencyForm from './forms/EmploymentFrequencyForm';
import AnnualIncomeForm from './forms/AnnualIncomeForm';
import EducationForm from './forms/EducationForm';
import EmailForm from './forms/EmailForm';
import BirthDateForm from './forms/BirthDateForm';
import ContactInfoForm from './forms/ContactInfoForm';
import PhoneVerificationForm from './forms/PhoneVerificationForm';
import SSNVerificationForm from './forms/SSNVerificationForm';
import SuccessStep from './forms/SuccessStep';
import ProgressBar from './ProgressBar';
import FormHeader from './FormHeader';
import LoadingOverlay from './LoadingOverlay';
import TrustpilotWidget from './TrustpilotWidget';
import ApplicationBlocked from './forms/ApplicationBlocked';
import ManualVerificationForm from './forms/ManualVerificationForm';
import UnsuccessfulApplication from './forms/UnsuccessfulApplication';
import { campaignVariantService } from '../services/campaignVariants';
import { formDefaultsService } from '../services/formDefaults';
import { clientConfig } from '../config/clientConfig';
import { trackFormStep } from '../utils/formTrack';

// Lazy load non-critical components
const DeveloperTools = lazy(() => import('./DeveloperTools'));
const SecurityTiles = lazy(() => import('./SecurityTiles'));
const TrustedPartners = lazy(() => import('./TrustedPartners'));

const MAX_CONTACT_ATTEMPTS = 3;
const SKIP_FREQUENCY_STATUSES = ['not_employed', 'retired', 'other'];

export default function MicroForm() {
  const { currentPath, navigateToRoute } = useFormRouting();

  const mockDelay = 5000;
  const delay = async () => {
    await new Promise((resolve) => setTimeout(resolve, mockDelay));
  };

  // Check for BPO agent flag in URL
  const [isBpoAgent] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('bpo') === 'true';
  });

  const [formController] = useState(() => {
    const controller = new FormController();
    // Set FormController reference in StorageController
    storageController.setFormController(controller);
    return controller;
  });

  const [routeManager] = useState(() => new FormRouteManager());
  const [formData, setFormData] = useState<FormData>(() => {
    const savedData = storageController.getFormData();
    const variant = campaignVariantService.getCurrentVariant();
    const defaults = formDefaultsService.getDefaults();
    
    const baseFormData: FormData = {
      email: savedData?.email || '',
      phone: savedData?.phone || '',
      birthDate: savedData?.birthDate || '',
      smsConsent: savedData?.smsConsent ?? true,
      promoSmsConsent: savedData?.promoSmsConsent || false,
      smsCode: savedData?.smsCode || '',
      ssnLast4: savedData?.ssnLast4 || '',
      loanAmount: savedData?.loanAmount || variant.defaultLoanAmount || 5000,
      loanPurpose: savedData?.loanPurpose || (variant.defaultLoanPurpose as any) || "debt_consolidation",
      propertyStatus: savedData?.propertyStatus || "rent",
      employmentFrequency: savedData?.employmentFrequency || "biweekly",
      employmentStatus: savedData?.employmentStatus || "employed_full_time",
      annualIncome: savedData?.annualIncome || 50000,
      educationLevel: savedData?.educationLevel || "associate",
      offerCode: savedData?.offerCode || variant.offerCode,
      firstName: savedData?.firstName || variant.firstName
    };
    
    // Apply form defaults
    return formDefaultsService.applyToFormData(baseFormData) as FormData;
  });

  const [validationErrors, setValidationErrors] = useState<{
    email: string | null;
    phone: string | null;
  }>({
    email: null,
    phone: null,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [contactAttempts, setContactAttempts] = useState(0);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [showManualVerification, setShowManualVerification] = useState(false);
  const [contactCenterAvailability, setContactCenterAvailability] = useState<
    'none' | 'low' | 'high' | null
  >(null);

  const currentStep = routeManager.getCurrentStep(currentPath);

  useEffect(() => {
    trackFormStep(currentPath as FormRoute);
  }, [currentPath]);
  // Initialize marketing params on first load
  useEffect(() => {
    try {
      // Initialize campaign variants first
      campaignVariantService.initializeFromUrl();
      formDefaultsService.initializeFromUrl();
      marketingParams.initializeFromUrl();
    } catch (err) {
      console.error('Failed to initialize marketing params:', err);
    }
  }, []);

  useEffect(() => {
    storageController.setFormData(formData);
  }, [formData]);

  useEffect(() => {
    if (contactAttempts >= MAX_CONTACT_ATTEMPTS) {
      setShowManualVerification(true);
      setError(null); // Clear any existing error when switching to manual verification
      navigateToRoute(FormRouteEnum.VERIFY_PHONE);
    }
  }, [contactAttempts]);

  useEffect(() => {
    if (verificationAttempts >= MAX_CONTACT_ATTEMPTS) {
      setShowManualVerification(true);
      setError(null); // Clear any existing error when switching to manual verification
    }
  }, [verificationAttempts]);

  useEffect(() => {
    if (
      storageController.isApplicationBlocked() &&
      !routeManager.isBlockedRoute(currentPath as FormRoute)
    ) {
      navigateToRoute(FormRouteEnum.BLOCK);
    }
  }, [currentPath, navigateToRoute]);

  // Check contact center availability and track success page when reaching success step
  useEffect(() => {
    const checkAvailability = async () => {
      if (currentStep === 13) {
        // Success step
        // Skip availability check for BPO agents
        if (isBpoAgent) {
          setContactCenterAvailability('none');
          return;
        }

        try {
          const result = await formController.checkContactCenterAvailability();
          if (result.success) {
            setContactCenterAvailability(result.availability);
          } else {
            setContactCenterAvailability('none');
          }
        } catch (err) {
          console.error('Failed to check contact center availability:', err);
          setContactCenterAvailability('none');
        }
      }
    };

    checkAvailability();
  }, [currentStep, isBpoAgent]);

  // Track which success page variant is shown
  useEffect(() => {
    if (currentStep === 13 && contactCenterAvailability !== null) {

    const availability = contactCenterAvailability;
      formController.successPage(availability).catch(() => void 0);
      // We intentionally ignore errors for UX; backend tracking failure shouldn't block UI
    }
  }, [currentStep, contactCenterAvailability]);

  const handleApplicationSuccess = () => {
    // Store the first name from the contact response
    if (formController.getContactFirstName()) {
      storageController.setContactFirstName(
        formController.getContactFirstName()
      );
    }

    storageController.setApplicationData({
      timestamp: Date.now(),
      email: formData.email,
      formData: {
        loanAmount: formData.loanAmount,
        loanPurpose: formData.loanPurpose,
        employmentStatus: formData.employmentStatus,
        propertyStatus: formData.propertyStatus,
        educationLevel: formData.educationLevel,
      },
      status: 'success',
    });
    storageController.clearFormData();
    storageController.setApplicationSuccess();
    navigateToRoute(FormRouteEnum.SUCCESS);
  };

  const handleScheduleAppointment = async (
    selectedTime: Date
  ): Promise<boolean> => {
    try {
      const result = await formController.scheduleAppointment(selectedTime);
      return result.success;
    } catch (err) {
      console.error('Failed to schedule appointment:', err);
      return false;
    }
  };

  const handleInitiateCall = async () => {
    try {
      const result = await formController.connectCall();
      return {
        success: result.success,
        from: result.from,
        error: result.error,
      };
    } catch (err) {
      console.error('Failed to connect call:', err);
      return {
        success: false,
        error: 'Failed to connect call. Please try again.',
      };
    }
  };

  const handleApplicationOffers = async (offerId: string, storeUuid: string) => {
    storageController.setApplicationData({
      timestamp: Date.now(),
      email: formData.email,
      formData: {
        loanAmount: formData.loanAmount,
        loanPurpose: formData.loanPurpose,
        employmentStatus: formData.employmentStatus,
        propertyStatus: formData.propertyStatus,
        educationLevel: formData.educationLevel,
      },
      status: 'success',
    });
    storageController.clearFormData();
    storageController.setApplicationOffers();

    setLoadingMessage('Loading your offers...');

    await delay();

    const targetUrl = `${clientConfig.offersUrl}/?id=${storeUuid}`;
    //const targetUrl = `https://fiona.com/partner/symple-lending-loans/loans?results=${offerId}&step=results`;
    window.location.href = targetUrl;
  };

  const handleApplicationFailure = () => {
    storageController.setApplicationData({
      timestamp: Date.now(),
      email: formData.email,
      formData: {
        loanAmount: formData.loanAmount,
        loanPurpose: formData.loanPurpose,
        employmentStatus: formData.employmentStatus,
        propertyStatus: formData.propertyStatus,
        educationLevel: formData.educationLevel,
      },
      status: 'unsuccessful',
    });
    storageController.clearFormData();
    storageController.setApplicationFailed();
    navigateToRoute(FormRouteEnum.UNSUCCESSFUL);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      const formattedPhone = formatPhoneNumber(value);
      setFormData((prev) => ({ ...prev, [name]: formattedPhone }));
      setValidationErrors((prev) => ({
        ...prev,
        phone: validatePhone(formattedPhone),
      }));
    } else if (name === 'email') {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setValidationErrors((prev) => ({
        ...prev,
        email: validateEmail(value),
      }));
    } else if (name === 'smsConsent') {
      setFormData((prev) => ({ ...prev, [name]: value === 'true' }));
    } else if (name === 'promoSmsConsent') {
      setFormData((prev) => ({ ...prev, [name]: value === 'true' }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Update marketing params with form data
    marketingParams.updateFormData(formData);
  };

  const handleConnectBySms = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailError = validateEmail(formData.email);
    const phoneError = validatePhone(formData.phone);

    setValidationErrors({
      email: emailError,
      phone: phoneError,
    });

    if (emailError || phoneError) {
      return;
    }

    // Update marketing params with form data
    marketingParams.updateFormData(formData);

    setLoading(true);
    setLoadingMessage('Creating secure connection...');
    setError(null);

    try {
      const ipAddress = storageController.getUserIp() || '';
      const result = await formController.connectBySms(
        formData.phone,
        formData.email,
        formData.birthDate,
        formData.loanAmount || 0,
        formData.loanPurpose || 'credit_card_refi',
        formData.employmentStatus || '',
        formData.employmentFrequency || '',
        formData.educationLevel || '',
        formData.annualIncome || 0,
        formData.propertyStatus || '',
        ipAddress,
        marketingParams.getParams()
      );

      if (result.success) {
        storageController.setApplicationSmsCode();
        navigateToRoute(FormRouteEnum.VERIFY_PHONE);
        setContactAttempts(0);
      } else {
        throw new Error(result.error || 'Failed to create connection');
      }
    } catch (err) {
      const newAttempts = contactAttempts + 1;
      setContactAttempts(newAttempts);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleCodeVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setLoadingMessage('Verifying SMS code...');
    setError(null);

    try {
      const verifyResult = await formController.verifyCode(
        formData.smsCode || '',
        formData
      );

      if (!verifyResult.success) {
        throw new Error(verifyResult.error);
      }

      setLoadingMessage('Checking eligibility...');
      const createResult = await formController.createContact(formData);

      if (!createResult.success) {
        if (createResult.status === 'ERROR' && createResult.code === 500) {
          setShowManualVerification(true);
          setError(null);
          return;
        }

        throw new Error(createResult.error);
      }

      switch (createResult.status) {
        case 'accepted':
          handleApplicationSuccess();
          break;
          
        case 'accepted_tier2':
          handleApplicationSuccess();
          break;

        case 'rejected':
          // Navigate to SSN verification (last 4) for rejected applications
          navigateToRoute(FormRouteEnum.VERIFY_SSN);
          break;

        case 'ERROR':
          if (formController.getVerificationAttempts() >= 3) {
            setShowManualVerification(true);
            setError(null); // Clear error when switching to manual verification
          } else {
            throw new Error(createResult.error || 'Verification failed');
          }
          break;
      }
    } catch (err) {
      setVerificationAttempts((prev) => prev + 1);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleManualVerification = async (data: Partial<FormData>): Promise<{ success: boolean; status?: string; error?: string }> => {
    setLoading(true);
    setLoadingMessage('Verifying information...');
    setError(null);

    try {
      data = formData;
      const result = await formController.verifyManually(data);

      if (result.success) {
        if (result.qualified) {
          handleApplicationSuccess();
          return { success: true, status: 'qualified' };
        } else {
          handleApplicationFailure();
          return { success: true, status: 'unqualified' };
        }
      } else {
        handleApplicationFailure();
        return { success: false, error: result.error || 'Verification failed' };
      }
    } catch (err) {
      handleApplicationFailure();
      return { success: false, error: err instanceof Error ? err.message : 'An error occurred' };
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleResendCode = async () => {
    // Update marketing params with form data
    marketingParams.updateFormData(formData);

    setLoading(true);
    setLoadingMessage('Sending new code...');
    setError(null);

    try {
      const ipAddress = storageController.getUserIp() || '';
      const result = await formController.connectBySms(
        formData.phone,
        formData.email,
        formData.birthDate,
        formData.loanAmount || 0,
        formData.loanPurpose || 'credit_card_refi',
        formData.employmentStatus || '',
        formData.employmentFrequency || '',
        formData.educationLevel || '',
        formData.annualIncome || 0,
        formData.propertyStatus || '',
        ipAddress,
        marketingParams.getParams()
      );

      if (result.success) {
        setFormData((prev) => ({ ...prev, smsCode: '' }));
      } else {
        throw new Error(result.error || 'Failed to send new code');
      }
    } catch (err) {
      const newAttempts = contactAttempts + 1;
      setContactAttempts(newAttempts);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleReenterInfo = () => {
    setFormData((prev) => ({ ...prev, smsCode: '' }));
    navigateToRoute(FormRouteEnum.BIRTH_DATE);
  };

  const handleFormStep = (data: Partial<FormData>) => {
    if (currentStep === 0) {
      storageController.setApplicationStarted();
    }

    setFormData((prev) => {
      
      const updatedData = { ...prev, ...data };
      
      // Fix: Use UPDATED data, not stale formData
      marketingParams.updateFormData(updatedData);
      
      return updatedData;
    });

    if (data.employmentStatus) {
      if (SKIP_FREQUENCY_STATUSES.includes(data.employmentStatus)) {
        setFormData((prev) => ({ ...prev, employmentFrequency: 'monthly' }));
        navigateToRoute(FormRouteEnum.ANNUAL_INCOME);
        return;
      }
    }

    const nextRoute = routeManager.getNextRoute(currentStep);
    if (nextRoute) {
      navigateToRoute(nextRoute);
    }
  };

  const handleOffersSubmit = async () => {
    setLoading(true);
    setLoadingMessage('Submitting application...');
    setError(null);

    try {
      const ipAddress = storageController.getUserIp() || '';
      const result = await formController.submitApplication(formData, ipAddress);
      
      if (result.success && result.offerId && result.storeUuid) {
        await handleApplicationOffers(result.offerId, result.storeUuid);
      } else {
        handleApplicationFailure();
      }
    } catch (err) {
      handleApplicationFailure();
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleBack = () => {
    setError(null);
    const prevRoute = routeManager.getPreviousRoute(currentStep);
    if (prevRoute) {
      navigateToRoute(prevRoute);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      {loading && <LoadingOverlay message={loadingMessage} />}

      <ProgressBar
        currentStep={currentStep}
        totalSteps={routeManager.getTotalSteps()}
        onBack={currentStep >= 2 ? handleBack : undefined}
      />

      <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-gray-100">
        <FormHeader />

        <div className="p-6">
          {currentStep === 0 && <StartForm onSubmit={handleFormStep} />}

          {currentStep === 1 && (
            <LoanAmountForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentStep === 2 && (
            <LoanPurposeForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentStep === 3 && (
            <PropertyStatusForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentStep === 4 && (
            <EmploymentStatusForm
              formData={formData}
              onSubmit={handleFormStep}
            />
          )}

          {currentStep === 5 && (
            <EmploymentFrequencyForm
              formData={formData}
              onSubmit={handleFormStep}
            />
          )}

          {currentStep === 6 && (
            <AnnualIncomeForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentStep === 7 && (
            <EducationForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentStep === 8 && (
            <EmailForm
              formData={formData}
              onChange={handleChange}
              onSubmit={handleFormStep}
            />
          )}

          {currentStep === 9 && (
            <BirthDateForm formData={formData} onSubmit={handleFormStep} />
          )}

          {currentStep === 10 && (
            <ContactInfoForm
              formData={formData}
              validationErrors={validationErrors}
              loading={loading}
              error={error}
              onChange={handleChange}
              onSubmit={handleConnectBySms}
            />
          )}

          {currentStep === 11 && !showManualVerification && (
            <PhoneVerificationForm
              formData={formData}
              loading={loading}
              error={error}
              onChange={handleChange}
              onSubmit={handleCodeVerification}
              onResendCode={handleResendCode}
              onReenterInfo={handleReenterInfo}
              verificationAttempts={verificationAttempts}
              onManualVerification={() => {
                setError(null);
                setShowManualVerification(true);
              }}
            />
          )}

          {currentStep === 11 && showManualVerification && (
            <ManualVerificationForm
              formData={formData}
              loading={loading}
              error={error}
              onSubmit={handleManualVerification}
              onChange={(data) => setFormData((prev) => ({ ...prev, ...data }))}
              onBack={() => {
                setShowManualVerification(false);
                setVerificationAttempts(0);
                setFormData((prev) => ({ ...prev, smsCode: '' }));
                setError(null);
              }}
            />
          )}

          {currentStep === 12 && (
            <SSNVerificationForm
              formData={formData}
              loading={loading}
              error={error}
              onChange={handleChange}
              onSubmit={handleOffersSubmit}
            />
          )}

          {currentStep === 13 && (
            <SuccessStep
              contactCenterAvailability={contactCenterAvailability}
              onScheduleAppointment={handleScheduleAppointment}
              onInitiateCall={handleInitiateCall}
              isBpoAgent={isBpoAgent}
            />
          )}

          {currentStep === 14 && <UnsuccessfulApplication />}

          {currentStep === 15 && (
            <ApplicationBlocked onInitiateCall={handleInitiateCall} />
          )}
        </div>
      </div>

      {currentStep === 0 && (
        <>
          <div className="mt-8 text-center">
            <TrustpilotWidget size="large" />
          </div>
        </>
      )}

      <Suspense fallback={null}>
        <SecurityTiles />
      </Suspense>

      <Suspense fallback={null}>
        <DeveloperTools formData={formData} />
      </Suspense>
    </div>
  );
}